import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'
import jwt from 'jwt-decode'

// Async Thunks for Password Reset
export const forgot_password = createAsyncThunk(
  'auth/forgot_password',
  async (email, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/customer/forgot-password', { email })
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const reset_password = createAsyncThunk(
  'auth/reset_password',
  async ({ token, password }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post(`/customer/reset-password/${token}`, { password })
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const change_password = createAsyncThunk(
  'auth/change_password',
  async ({ oldPassword, newPassword }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.put('/customer/change-password', { oldPassword, newPassword })
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

// Existing Async Thunks for Register, Login, etc.

export const customer_register = createAsyncThunk(
  'auth/customer_register',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/customer/customer-register', info)
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const customer_login = createAsyncThunk(
  'auth/customer_login',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/customer/customer-login', info)
      localStorage.setItem('customerToken', data.token)
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const verify_email = createAsyncThunk(
  'auth/verify_email',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/customer/verify-email', info)
      localStorage.setItem('customerToken', data.token)
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const resend_verification = createAsyncThunk(
  'auth/resend_verification',
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post('/customer/resend-verification', info)
      return fulfillWithValue(data)
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

// Safe Token Decoder
const decodeToken = (token) => {
  try {
    if (token && typeof token === 'string' && token.trim() !== '') {
      const decoded = jwt(token)
      return {
        ...decoded,
        verified: decoded.verified || false
      }
    }
  } catch (err) {
    console.error('Invalid token in decodeToken:', err)
  }
  return null
}

// Redux Slice
export const authReducer = createSlice({
  name: 'auth',
  initialState: {
    loader: false,
    userInfo: decodeToken(localStorage.getItem('customerToken')),
    errorMessage: '',
    successMessage: ''
  },
  reducers: {
    messageClear: (state) => {
      state.errorMessage = ''
      state.successMessage = ''
    },
    user_reset: (state) => {
      state.userInfo = null
      localStorage.removeItem('customerToken')
    }
  },
  extraReducers: (builder) => {
    builder
      // Forgot Password
      .addCase(forgot_password.pending, (state) => {
        state.loader = true
      })
      .addCase(forgot_password.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Failed to send reset link.'
        state.loader = false
      })
      .addCase(forgot_password.fulfilled, (state, { payload }) => {
        state.successMessage = payload?.message || 'Reset link sent to your email.'
        state.loader = false
      })

      // Reset Password
      .addCase(reset_password.pending, (state) => {
        state.loader = true
      })
      .addCase(reset_password.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Failed to reset password.'
        state.loader = false
      })
      .addCase(reset_password.fulfilled, (state, { payload }) => {
        state.successMessage = payload?.message || 'Password reset successful.'
        state.loader = false
      })

      // Register
      .addCase(customer_register.pending, (state) => {
        state.loader = true
      })
      .addCase(customer_register.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Registration failed.'
        state.loader = false
      })
      .addCase(customer_register.fulfilled, (state, { payload }) => {
        state.successMessage = payload.message
        state.loader = false
      })

      // Login
      .addCase(customer_login.pending, (state) => {
        state.loader = true
      })
      .addCase(customer_login.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Login failed.'
        state.loader = false
      })
      .addCase(customer_login.fulfilled, (state, { payload }) => {
        state.userInfo = decodeToken(payload.token)
        state.successMessage = payload.message
        state.loader = false
      })

      // Verify Email
      .addCase(verify_email.pending, (state) => {
        state.loader = true
      })
      .addCase(verify_email.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Verification failed.'
        state.loader = false
      })
      .addCase(verify_email.fulfilled, (state, { payload }) => {
        state.userInfo = decodeToken(payload.token)
        state.successMessage = payload.message
        state.loader = false
      })

      // Resend Verification
      .addCase(resend_verification.pending, (state) => {
        state.loader = true
      })
      .addCase(resend_verification.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || 'Failed to resend verification.'
        state.loader = false
      })
      .addCase(resend_verification.fulfilled, (state, { payload }) => {
        state.successMessage = payload.message
        state.loader = false
      })

      // Change Password
      .addCase(change_password.pending, (state) => {
        state.loader = true
      })
      .addCase(change_password.rejected, (state, { payload }) => {
        state.errorMessage = payload?.error || payload?.message || 'Failed to change password.'
        state.loader = false
      })
      .addCase(change_password.fulfilled, (state, { payload }) => {
        state.successMessage = payload.message || 'Password changed successfully.'
        state.loader = false
      })
  }
})

export const { messageClear, user_reset } = authReducer.actions
export default authReducer.reducer
